import { Dialog } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/buttons';
import { Panel } from 'components/containers';
import IconCross from 'components/icons/cross';

import { AccountType } from 'types/account';

import { CreateAccountModalForm, CreateAccountModalSuccess, CreateAccountModalVerify } from './components';
import { isCreateAccountFormComplete } from './isCreateAccountFormComplete';
import { LABELS } from './keys';
import { Buttons, Delimeter, ModalControls, ModalRoot, PanelContent } from './styles';
import { CreateAccountStep } from './types';
import { useCreateAccountModal } from './useCreateAccountModal';

export const CreateAccountModal = ({
  error,
  isSubmitting,
  reset,
  open,
  onClose,
  account,
  onCreateAccount,
  setAccountType,
  accountType,
  getGroupAccounts,
  pwcCustomerType,
  setPwcCustomerType,
  getParentAccounts,
}: ReturnType<typeof useCreateAccountModal>['modalProps']) => {
  const { t } = useTranslation();

  const [currentStep, setCurrentStep] = useState<CreateAccountStep>(CreateAccountStep.Form);
  const [formValues, setFormValues] = useState(account);

  useEffect(() => {
    if (open && account) {
      setFormValues(account);
      setCurrentStep(CreateAccountStep.Form);
    }
  }, [open, account]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setFormValues((prev) => ({
      ...prev,
      ...(accountType === AccountType.Pwc_Group ? { groupAccountId: '' } : { parentAccountUuid: '', parentAccountName: undefined }),
    }));
  }, [accountType, open]);

  const handleFormValueChange = (fieldName: string, value: any) => {
    setFormValues((prevState) => ({
      ...prevState,
      [fieldName]: value,
    }));
  };

  const closeModal = () => {
    reset();
    onClose();
  };

  const handleCreateAccount = async () => {
    const response = await onCreateAccount(formValues);

    if (response) {
      goToSuccessStep();
    } else {
      goToFormStep();
    }
  };

  const goToFormStep = () => {
    setCurrentStep(CreateAccountStep.Form);
  };
  const goToVerifyStep = () => {
    setCurrentStep(CreateAccountStep.Verify);
  };

  const goToSuccessStep = () => {
    setCurrentStep(CreateAccountStep.Success);
  };

  const formIsComplete = isCreateAccountFormComplete(formValues, accountType, pwcCustomerType);

  const getModalContent = () => {
    switch (currentStep) {
      case CreateAccountStep.Verify:
        return <CreateAccountModalVerify pwcCustomerType={pwcCustomerType} accountType={accountType} formValues={formValues} />;
      case CreateAccountStep.Success:
        return <CreateAccountModalSuccess formValues={formValues} />;

      default:
        return (
          <CreateAccountModalForm
            pwcCustomerType={pwcCustomerType}
            setPwcCustomerType={setPwcCustomerType}
            getGroupAccounts={getGroupAccounts}
            getParentAccounts={getParentAccounts}
            setAccountType={setAccountType}
            accountType={accountType}
            formValues={formValues}
            onFieldValueChange={handleFormValueChange}
            error={error}
          />
        );
    }
  };
  const getModalButtons = () => {
    switch (currentStep) {
      case CreateAccountStep.Verify:
        return (
          <Buttons>
            <Button secondary onClick={goToFormStep} disabled={isSubmitting}>
              {t(LABELS.CREATE_MODAL.BUTTONS.EDIT)}
            </Button>
            <Button primary onClick={handleCreateAccount} disabled={isSubmitting}>
              {t(LABELS.CREATE_MODAL.BUTTONS.CONFIRM)}
            </Button>
          </Buttons>
        );

      case CreateAccountStep.Success:
        return (
          <Buttons>
            <Button primary onClick={closeModal} disabled={isSubmitting}>
              {t(LABELS.CREATE_MODAL.BUTTONS.CLOSE)}
            </Button>
          </Buttons>
        );

      default:
        return (
          <Buttons>
            <Button secondary onClick={closeModal} disabled={isSubmitting}>
              {t(LABELS.CREATE_MODAL.BUTTONS.CANCEL)}
            </Button>
            <Button primary onClick={goToVerifyStep} disabled={isSubmitting || !formIsComplete}>
              {t(LABELS.CREATE_MODAL.BUTTONS.CREATE)}
            </Button>
          </Buttons>
        );
    }
  };

  const modalContent = getModalContent();
  const modalButtons = getModalButtons();

  return (
    <Dialog open={open} scroll="body" onClose={closeModal}>
      <ModalRoot>
        <Panel
          label={t(LABELS.CREATE_MODAL.TITLE)}
          controls={
            <ModalControls>
              <Button flat onClick={closeModal}>
                <IconCross />
              </Button>
            </ModalControls>
          }
        >
          <PanelContent>
            {modalContent}
            <Delimeter />
            {modalButtons}
          </PanelContent>
        </Panel>
      </ModalRoot>
    </Dialog>
  );
};
